import { FC, ReactNode } from 'react'
import Link from 'next/link'
import { Box, Button } from '@mui/material'

interface NavPillButtonProps {
  href: string
  label: string
  icon: ReactNode
  ariaLabel?: string
  color?: string
  borderColor?: string
  compactOnMobile?: boolean
}

export const NavPillButton: FC<NavPillButtonProps> = ({
  href,
  label,
  icon,
  ariaLabel,
  color = 'white',
  borderColor = 'rgba(255, 255, 255, 0.18)',
  compactOnMobile = false,
}) => (
  <Button
    component={Link}
    href={href}
    startIcon={icon}
    aria-label={ariaLabel ?? (compactOnMobile ? label : undefined)}
    sx={{
      height: 40,
      px: 2,
      gap: 0.25,
      borderRadius: '999px',
      color,
      fontSize: '0.875rem',
      fontWeight: 700,
      letterSpacing: '0.01em',
      textTransform: 'none',
      whiteSpace: 'nowrap',
      bgcolor: 'rgba(255, 255, 255, 0.1)',
      border: `1px solid ${borderColor}`,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.18)',
      transition: 'background-color 150ms ease, border-color 150ms ease, transform 150ms ease',
      '& .MuiButton-startIcon': { mr: 0.75 },
      '&:hover': {
        bgcolor: 'rgba(255, 255, 255, 0.18)',
        borderColor: 'rgba(255, 255, 255, 0.32)',
      },
      '&:active': { transform: 'scale(0.97)' },
      '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 },
      ...(compactOnMobile && {
        width: { xs: 40, sm: 'auto' },
        minWidth: { xs: 40, sm: 64 },
        px: { xs: 0, sm: 2 },
        flexShrink: 0,
        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.75 }, ml: { xs: 0, sm: -0.5 } },
      }),
    }}
  >
    {compactOnMobile ? (
      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
        {label}
      </Box>
    ) : (
      label
    )}
  </Button>
)
